import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../utils/constants';
import * as adminService from '../services/adminService';
import { syncPendingToBackend, syncAllToBackend } from '../services/adminService';
import { adminFetch } from '../services/apiClient';
import * as subService from '../services/subscriptionService';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) throw new Error('useAdmin must be used within AdminProvider');
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const token = localStorage.getItem('adminToken');
    // Old sessions used literal 'logged_in' — force re-login with new JWT-based auth
    return !!token && token !== 'logged_in' && token.length > 30;
  });

  const [articles, setArticles] = useState(() => adminService.getAllAdminArticles());

  const refreshArticles = useCallback(async () => {
    try {
      const res = await adminFetch('/api/admin/articles');
      // 401 means token is expired/invalid — adminFetch already fired the event,
      // but also handle it here to immediately stop further work in this call.
      if (res.status === 401) return [];
      if (res.ok) {
        const data = await res.json();
        const list = data.articles || [];
        setArticles(list);
        return list;
      }
    } catch {}
    setArticles(adminService.getAllAdminArticles());
  }, []);

  // Auto-logout when any adminFetch call returns 401 (expired or invalid token).
  // adminFetch dispatches 'admin:unauthorized' on every 401 response.
  useEffect(() => {
    const handle = () => {
      localStorage.removeItem('adminToken');
      setIsLoggedIn(false);
    };
    window.addEventListener('admin:unauthorized', handle);
    return () => window.removeEventListener('admin:unauthorized', handle);
  }, []);

  // On login: flush pending-sync queue, do full migration, refresh articles from backend
  useEffect(() => {
    if (isLoggedIn) {
      syncPendingToBackend().catch(() => {});
      syncAllToBackend().catch(() => {});
      refreshArticles().catch(() => {});
    }
  }, [isLoggedIn, refreshArticles]);

  const login = useCallback(async (username, password) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        signal: AbortSignal.timeout(60000),
      });
      const isJson = (res.headers.get('content-type') || '').includes('application/json');
      const data = isJson ? await res.json() : {};
      if (!res.ok) return { success: false, message: data.error || 'गलत यूजरनेम या पासवर्ड!' };
      localStorage.setItem('adminToken', data.token);
      setIsLoggedIn(true);
      return { success: true };
    } catch (err) {
      const isTimeout = err?.name === 'TimeoutError' || err?.name === 'AbortError';
      const msg = isTimeout
        ? 'सर्वर शुरू हो रहा है, कृपया 30 सेकंड बाद फिर कोशिश करें।'
        : 'सर्वर से कनेक्ट नहीं हो पाया। पुनः प्रयास करें।';
      return { success: false, message: msg };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
  }, []);

  const getArticles = useCallback(() => articles, [articles]);

  const getArticle = useCallback((id) => {
    return articles.find(a => a.id === id) || adminService.getAdminArticle(id);
  }, [articles]);

  const addArticle = useCallback((data) => {
    const result = adminService.addArticle(data);
    // Use the returned article (which still has its full base64 image in memory)
    // rather than reloading from localStorage, which strips base64 to prevent quota issues.
    setArticles((prev) => [result, ...prev.filter((a) => a.id !== result.id)]);
    refreshArticles().catch(() => {});
    return result;
  }, [refreshArticles]);

  const updateArticle = useCallback((id, data) => {
    const result = adminService.updateArticle(id, data);
    // Same reason as addArticle — use in-memory result, not stripped localStorage copy.
    setArticles((prev) => prev.map((a) => (a.id === id ? result : a)));
    refreshArticles().catch(() => {});
    return result;
  }, [refreshArticles]);

  const deleteArticle = useCallback((id) => {
    const result = adminService.deleteArticle(id);
    setArticles(adminService.getAllAdminArticles());
    refreshArticles().catch(() => {});
    return result;
  }, [refreshArticles]);

  const toggleVisibility = useCallback((id) => {
    const result = adminService.toggleArticleVisibility(id);
    setArticles(adminService.getAllAdminArticles());
    refreshArticles().catch(() => {});
    return result;
  }, [refreshArticles]);

  const getSubscribers = useCallback(() => {
    return subService.getAllSubscribers();
  }, []);

  const addSubscriber = useCallback((email, mobile) => {
    return subService.addSubscriber(email, mobile);
  }, []);

  const exportSubscribers = useCallback(() => {
    return subService.exportSubscribersCSV();
  }, []);

  const value = {
    isLoggedIn,
    login,
    logout,
    getArticles,
    getArticle,
    addArticle,
    updateArticle,
    deleteArticle,
    toggleVisibility,
    getSubscribers,
    addSubscriber,
    exportSubscribers,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
